import { Box, Dialog, Typography } from '@mui/material'
import { Edit02Icon, XIcon } from '@untitled-theme/icons-react'
import React from 'react'

import TextButton from '@/shared/components/TextButton/TextButton'

import type { InfoPopupProps } from './InfoPopup.types'

interface BaseInfoPopupProps {
  title: string
  subtitle: React.ReactNode
  description?: React.ReactNode
  onEdit?: () => void
  onClose: () => void
  open: boolean
}

// Base popup component - NOT exported
const BaseInfoPopup: React.FC<BaseInfoPopupProps> = ({
  title,
  subtitle,
  description,
  onEdit,
  onClose,
  open,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            border: '1px solid #E1E1E4',
            boxShadow: 'none',
            margin: '24px',
            maxWidth: '500px',
          },
        },
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingX: 2,
          paddingY: 3,
          gap: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '24px',
            }}
          >
            {title}
          </Typography>
          <XIcon width={24} height={24} onClick={onClose} style={{ cursor: 'pointer' }} />
        </Box>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '14px',
          }}
        >
          {subtitle}
        </Typography>
        {description && (
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '14px',
              color: '#74777F',
            }}
          >
            {description}
          </Typography>
        )}
        {onEdit && (
          <TextButton
            height="48px"
            sx={{ paddingX: 3, gap: '8px', border: '1px solid #181D29', alignSelf: 'center' }}
            onClick={onEdit}
          >
            Daten bearbeiten
            <Edit02Icon width={24} height={24} />
          </TextButton>
        )}
      </Box>
    </Dialog>
  )
}

export const EnergyDemandInfoPopup: React.FC<InfoPopupProps> = ({ open, onClose, onEdit }) => {
  return (
    <BaseInfoPopup
      title="Energiebedarf"
      subtitle="Der Energiebedarf eines Gebäudes beschreibt die Energiemenge, die für Heizung, Warmwasser, Lüftung und ggf. Kühlung benötigt wird. Er zeigt, wie effizient ein Gebäude ist und dient als Grundlage zur Bewertung des energetischen Zustands."
      description="Der Wert erscheint Ihnen nicht realistisch? Klicken Sie auf „Daten bearbeiten“, um in die Dateneinstellungen zu gelangen."
      onEdit={onEdit}
      open={open}
      onClose={onClose}
    />
  )
}

export const EnergyConsumptionInfoPopup: React.FC<InfoPopupProps> = ({ open, onClose, onEdit }) => {
  return (
    <BaseInfoPopup
      title="Energieverbrauch"
      subtitle="Der Energieverbrauch eines Gebäudes beschreibt die tatsächlich genutzte Energiemenge für Heizung, Warmwasser, Lüftung und andere technische Anlagen. Er hängt vom Nutzerverhalten und den Betriebsbedingungen ab und kann vom berechneten Energiebedarf abweichen."
      description="Der Wert erscheint Ihnen nicht realistisch? Klicken Sie auf „Daten bearbeiten“, um in die Dateneinstellungen zu gelangen."
      onEdit={onEdit}
      open={open}
      onClose={onClose}
    />
  )
}

export const SavingsPotentialInfoPopup: React.FC<InfoPopupProps> = ({ open, onClose, onEdit }) => {
  return (
    <BaseInfoPopup
      title="Einsparpotenzial"
      subtitle={
        <>
          Zur Berechnung wird der aktuelle Energieverbrauch mit dem voraussichtlichen Verbrauch nach
          der Sanierung verglichen.{' '}
          <strong>
            Die Einsparung ergibt sich aus der Differenz der alten und neuen Energiekosten.
          </strong>{' '}
          Die Energiepreise und zukünftige Entwicklungen des CO2-Preises werden dabei
          berücksichtigt, um die Einsparungen möglichst genau zu schätzen.
        </>
      }
      description="Der Wert erscheint Ihnen nicht realistisch? Klicken Sie auf „Daten bearbeiten“, um in die Dateneinstellungen zu gelangen."
      onEdit={onEdit}
      open={open}
      onClose={onClose}
    />
  )
}

export const RenovationCostsInfoPopup: React.FC<InfoPopupProps> = ({ open, onClose, onEdit }) => {
  return (
    <BaseInfoPopup
      title="Sanierungskosten"
      subtitle="Die Sanierungskosten werden anhand wissenschaftlicher Kostenfunktionen berechnet, die wichtige Faktoren wie Dämmfläche, Dämmstärke und Anlagenleistung berücksichtigen. Anschließend werden die Ergebnisse über Regionalfaktoren an regionale Preisunterschiede in Deutschland angepasst."
      description="Der Wert erscheint Ihnen nicht realistisch? Klicken Sie auf „Daten bearbeiten“, um in die Dateneinstellungen zu gelangen."
      onEdit={onEdit}
      open={open}
      onClose={onClose}
    />
  )
}

export const MaximumFundingInfoPopup: React.FC<InfoPopupProps> = ({ open, onClose }) => {
  return (
    <BaseInfoPopup
      title="Maximale Förderung"
      subtitle="Die Anzeige der maximalen Förderung basiert auf einer förderoptimierten Sanierungsweise. Hierbei werden die jährlichen förderfähigen Kosten von 60.000 Euro pro Wohneinheit maximal ausgenutzt."
      description="Werden alle Maßnahmen in einem Jahr umgesetzt, kann die Förderhöhe abweichen und geringer sein."
      open={open}
      onClose={onClose}
    />
  )
}
